import { AlertTriangle, CheckCircle2, HelpCircle, OctagonAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { tx } from '../i18n';
import type { CategoryDetail, ConfidenceLevel, Severity, VerdictLabel } from '../types/api';
import { isUnrated } from './report';

/**
 * The one status vocabulary the whole interface speaks.
 *
 * <p>Four states, and only four: good, something to check, a risk, and no data. Every screen that
 * has a view about a number resolves it to one of these, so the same situation is never green on
 * one card and amber on the next. A status is never carried by colour alone - {@link statusIcon}
 * and {@link statusLabel} travel with it everywhere it is drawn, which is what makes the report
 * readable in greyscale, at a glance, and to a screen reader.
 */
export type Status = 'good' | 'attention' | 'risk' | 'unknown';

/** The icon that stands for a status. Distinct in shape, not only in colour. */
export function statusIcon(status: Status): LucideIcon {
  switch (status) {
    case 'good':
      return CheckCircle2;
    case 'attention':
      return AlertTriangle;
    case 'risk':
      return OctagonAlert;
    default:
      return HelpCircle;
  }
}

/** The word for a status, for the chip and for the screen reader. */
export function statusLabel(status: Status): string {
  switch (status) {
    case 'good':
      return tx('Good', 'İyi', 'Dobrze', 'Bom', 'Bien');
    case 'attention':
      return tx('Worth checking', 'Kontrol edin', 'Warto sprawdzić', 'A verificar', 'A comprobar');
    case 'risk':
      return tx('Risk', 'Risk', 'Ryzyko', 'Risco', 'Riesgo');
    default:
      return tx('No data', 'Veri yok', 'Brak danych', 'Sem dados', 'Sin datos');
  }
}

/**
 * This status's colour for text and small marks: the darkened value, which clears 4.5:1 on the
 * page background at any size.
 */
export function statusColour(status: Status): string {
  return `var(--status-${status})`;
}

/** The same status as a fill for a bar or a ring, where the brand value clears the 3:1 it needs. */
export function statusFill(status: Status): string {
  return `var(--status-${status}-fill)`;
}

/** A 0-100 score as a status. The thresholds are the ones the verdict itself uses. */
export function statusOfScore(score: number): Status {
  if (score >= 70) {
    return 'good';
  }
  return score >= 55 ? 'attention' : 'risk';
}

/** A category as a status, so an unrated one is grey rather than a bad red. */
export function statusOfCategory(category: CategoryDetail): Status {
  return isUnrated(category) ? 'unknown' : statusOfScore(category.score);
}

/** A finding's severity as a status: the API's three levels map one to one. */
export function statusOfSeverity(severity: Severity): Status {
  switch (severity) {
    case 'WARNING':
      return 'risk';
    case 'ATTENTION':
      return 'attention';
    default:
      return 'good';
  }
}

/** The overall verdict as a status. */
export function statusOfVerdict(label: VerdictLabel): Status {
  switch (label) {
    case 'EXCELLENT':
    case 'GOOD':
      return 'good';
    case 'FAIR':
      return 'attention';
    default:
      return 'risk';
  }
}

/**
 * How far a number can be trusted, which is a different question from whether it is good.
 *
 * <p>A tenant reads "Safety 42" as a fact about the street; it is only a fact if the police data
 * actually covered this address for the whole period. Separating the two means a low score and a
 * shaky measurement never look the same.
 */
export type Quality = 'verified' | 'estimated' | 'unavailable';

export function qualityLabel(quality: Quality): string {
  switch (quality) {
    case 'verified':
      return tx('Verified', 'Doğrulandı', 'Zweryfikowane', 'Verificado', 'Verificado');
    case 'estimated':
      return tx('Estimated', 'Tahmini', 'Szacowane', 'Estimado', 'Estimado');
    default:
      return tx('Unavailable', 'Alınamadı', 'Niedostępne', 'Indisponível', 'No disponible');
  }
}

/** What each label actually means, as the title attribute and the sources section both need it. */
export function qualityMeaning(quality: Quality): string {
  switch (quality) {
    case 'verified':
      return tx(
        'Registered against this exact address.',
        'Tam olarak bu adrese kayıtlı.',
        'Zarejestrowane pod tym konkretnym adresem.',
        'Registado nesta morada exata.',
        'Registrado en esta dirección exacta.',
      );
    case 'estimated':
      return tx(
        'Measured around the address rather than at it, or scaled from a shorter period.',
        'Adresin kendisinde değil çevresinde ölçüldü ya da daha kısa bir dönemden ölçeklendi.',
        'Zmierzone w okolicy adresu, a nie pod nim, albo przeliczone z krótszego okresu.',
        'Medido à volta da morada e não nela, ou extrapolado de um período mais curto.',
        'Medido alrededor de la dirección y no en ella, o escalado desde un periodo más corto.',
      );
    default:
      return tx(
        'The source could not be read for this address.',
        'Kaynak bu adres için okunamadı.',
        'Nie udało się odczytać źródła dla tego adresu.',
        'Não foi possível ler a fonte para esta morada.',
        'No se ha podido leer la fuente para esta dirección.',
      );
  }
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * How well a category's own measurement pins down this dwelling.
 *
 * <p>Each category is estimated for its own reason, and the reason is what the reader needs:
 * broadband is sold per postcode and measured per postcode, crime is counted in a circle around
 * the address, and an energy certificate is the one source registered against the door itself.
 */
export function qualityOfCategory(category: CategoryDetail): Quality {
  if (isUnrated(category)) {
    return 'unavailable';
  }
  const m = category.metrics;
  switch (category.category) {
    case 'COMFORT':
      return m.exactAddressMatch === false ? 'estimated' : 'verified';
    case 'CONNECTIVITY': {
      const level = typeof m.matchLevel === 'string' ? m.matchLevel.toUpperCase() : null;
      return level === 'POSTCODE' ? 'verified' : 'estimated';
    }
    // Crime is counted in a circle around the address and never at it, so it is never "verified"
    // however complete the data is. Saying otherwise would be the wrong kind of confident.
    case 'SAFETY':
      return 'estimated';
    default:
      return 'estimated';
  }
}

/** Why a category carries the label it carries, in one line under the number. */
export function qualityReason(category: CategoryDetail): string {
  const m = category.metrics;
  if (isUnrated(category)) {
    return qualityMeaning('unavailable');
  }
  switch (category.category) {
    case 'COMFORT':
      return m.exactAddressMatch === false
        ? tx(
            'The nearest certificate to this address, not one lodged against it.',
            'Bu adrese değil, ona en yakın konuta ait sertifika.',
            'Najbliższe świadectwo, a nie wystawione pod tym adresem.',
            'O certificado mais próximo, não um emitido para esta morada.',
            'El certificado más cercano, no uno emitido para esta dirección.',
          )
        : tx(
            'From the certificate lodged against this address.',
            'Bu adrese kayıtlı sertifikadan.',
            'Ze świadectwa wystawionego pod tym adresem.',
            'Do certificado emitido para esta morada.',
            'Del certificado emitido para esta dirección.',
          );
    case 'CONNECTIVITY': {
      const level = typeof m.matchLevel === 'string' ? m.matchLevel.toUpperCase() : null;
      if (level === 'POSTCODE') {
        return tx(
          'Ofcom measures broadband per postcode, and this is your postcode.',
          "Ofcom internet altyapısını posta kodu bazında ölçer; bu da sizin posta kodunuz.",
          'Ofcom mierzy internet dla kodu pocztowego — i to jest Twój kod.',
          'A Ofcom mede a internet por código postal, e este é o seu.',
          'Ofcom mide la internet por código postal, y este es el tuyo.',
        );
      }
      return tx(
        'Measured across a wider area than this postcode.',
        'Bu posta kodundan daha geniş bir alanda ölçüldü.',
        'Zmierzone na obszarze szerszym niż ten kod pocztowy.',
        'Medido numa área mais ampla do que este código postal.',
        'Medido en un área más amplia que este código postal.',
      );
    }
    case 'SAFETY': {
      const radius = num(m.radiusMetres) ?? 500;
      const covered = num(m.monthsCovered);
      const requested = num(m.monthsRequested);
      if (covered !== null && requested !== null && covered < requested) {
        return tx(
          `Counted in a ${radius} m circle, and only ${covered} of ${requested} months were published.`,
          `${radius} m yarıçapında sayıldı ve ${requested} ayın yalnızca ${covered} ayı yayımlanmış.`,
          `Policzone w promieniu ${radius} m, a opublikowano tylko ${covered} z ${requested} miesięcy.`,
          `Contados num raio de ${radius} m, e só ${covered} dos ${requested} meses foram publicados.`,
          `Contados en un radio de ${radius} m, y solo ${covered} de ${requested} meses se han publicado.`,
        );
      }
      return tx(
        `Counted in a ${radius} m circle around the address, not at the address.`,
        `Adreste değil, adres çevresinde ${radius} m yarıçapında sayıldı.`,
        `Policzone w promieniu ${radius} m wokół adresu, nie pod samym adresem.`,
        `Contados num raio de ${radius} m à volta da morada, não na morada.`,
        `Contados en un radio de ${radius} m alrededor de la dirección, no en ella.`,
      );
    }
    default:
      return qualityMeaning('estimated');
  }
}

/** Confidence as a status, for the chip beside the overall score. */
export function statusOfConfidence(level: ConfidenceLevel): Status {
  switch (level) {
    case 'HIGH':
      return 'good';
    case 'MEDIUM':
      return 'attention';
    default:
      return 'unknown';
  }
}
